import { useState } from 'react';
import {
  DIAPER_KINDS,
  DIAPER_CONSISTENCIES,
  DIAPER_COLORS,
} from '@shared/constants.ts';
import type { DiaperKind, DiaperConsistency, DiaperColor } from '@shared/constants.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, SegRow, ToggleSwitch } from './SheetShell.tsx';

type Props = {
  open: boolean;
  onClose: () => void;
  defaultKind?: DiaperKind;
  onSubmit: (vars: {
    kind: DiaperKind;
    consistency?: DiaperConsistency | null;
    color?: DiaperColor | null;
    leak: boolean;
    notes?: string;
  }) => Promise<unknown> | void;
};

const cap = (s: string) => s[0]?.toUpperCase() + s.slice(1);

export function DiaperSheet({ open, onClose, defaultKind = 'pee', onSubmit }: Props) {
  const [kind, setKind] = useState<DiaperKind>(defaultKind);
  const [consistency, setConsistency] = useState<DiaperConsistency | null>(null);
  const [color, setColor] = useState<DiaperColor | null>(null);
  const [leak, setLeak] = useState(false);
  const [notes, setNotes] = useState('');

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={kind === 'poop' ? 'Log poop' : kind === 'pee' ? 'Log pee' : 'Log diaper'}
      tagline=""
      accent={kind === 'pee' ? 'pee' : 'poop'}
      icon={(p) => <Icon.diaper {...p} />}
      primaryLabel="Log diaper"
      onPrimary={async () => {
        await onSubmit({
          kind,
          consistency: kind === 'pee' ? null : consistency,
          color: kind === 'pee' ? null : color,
          leak,
          notes: notes.trim() || undefined,
        });
        onClose();
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SegRow
          label="Kind"
          active={kind}
          onChange={(v) => setKind(v)}
          options={DIAPER_KINDS.map((id) => ({
            id,
            label: cap(id),
            dot: true,
            color: id === 'pee' ? 'pee' : 'poop',
          }))}
        />
        {kind !== 'pee' && (
          <>
            <SegRow
              label="Consistency"
              active={(consistency ?? 'soft') as DiaperConsistency}
              onChange={(v) => setConsistency(v)}
              options={DIAPER_CONSISTENCIES.map((id) => ({ id, label: cap(id) }))}
            />
            <SegRow
              label="Colour"
              active={(color ?? 'yellow') as DiaperColor}
              onChange={(v) => setColor(v)}
              options={DIAPER_COLORS.filter((c) => c !== 'other').map((id) => ({
                id,
                label: cap(id),
              }))}
            />
          </>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderRadius: 14,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Leak</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Onto outfit / sheets?</div>
          </div>
          <ToggleSwitch on={leak} onChange={setLeak} />
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.04,
              marginBottom: 6,
              paddingLeft: 4,
            }}
          >
            Notes
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="optional"
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 14,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: 'var(--text-1)',
              fontSize: 14,
              resize: 'none',
            }}
          />
        </div>
      </div>
    </SheetShell>
  );
}
